import { apiRequest } from '@/lib/api/client';
import type {
  AuthorArticle,
  CatalogResolved,
  ClientAnswerItem,
  DirectoryUser,
  EditorialProject,
  FeedPost,
  ResearchDraft,
  ResearchQuestion,
  ResearchRecommendationsResult,
  ResearchSubmitPayload,
  ResearchSubmitResult,
} from './types';

export const fetchGatewayDirectory = (search?: string, limit = 50): Promise<DirectoryUser[]> =>
  apiRequest<DirectoryUser[]>('/request-gateway/directory/users', {
    channel: 'platform',
    query: { search: search || undefined, limit },
  });

export const fetchGatewayCatalog = (role?: string): Promise<CatalogResolved> =>
  apiRequest<CatalogResolved>('/request-gateway/catalog', {
    channel: 'platform',
    query: { role },
  });

export const lookupClientAnswers = (
  reqCode: string,
  role?: string
): Promise<{ found: boolean; record: ClientAnswerItem | null; message: string }> =>
  apiRequest<{ found: boolean; record: ClientAnswerItem | null; message: string }>(
    '/request-gateway/client-answers/lookup',
    {
      channel: 'platform',
      query: { req_code: reqCode, role },
    }
  );

export const listClientAnswers = (
  limit = 20,
  role?: string
): Promise<{ items: ClientAnswerItem[]; total: number }> =>
  apiRequest<{ items: ClientAnswerItem[]; total: number }>(
    '/request-gateway/client-answers',
    {
      channel: 'platform',
      query: { limit, role },
    }
  );

export const saveResearchDraft = (
  payload: Partial<ResearchDraft>,
  role?: string
): Promise<ResearchDraft> =>
  apiRequest<ResearchDraft>('/request-gateway/research/draft', {
    method: 'POST',
    channel: 'platform',
    query: { role },
    body: payload,
  });

export const getResearchDraft = (role?: string): Promise<ResearchDraft> =>
  apiRequest<ResearchDraft>('/request-gateway/research/draft', {
    channel: 'platform',
    query: { role },
  });

export const fetchResearchQuestions = (
  serviceId: string,
  role?: string
): Promise<{ service_id: string; questions: ResearchQuestion[] }> =>
  apiRequest<{ service_id: string; questions: ResearchQuestion[] }>(
    '/request-gateway/research/questions',
    {
      channel: 'platform',
      query: { service_id: serviceId, role },
    }
  );

export const fetchResearchRecommendations = (
  payload: { service_id: string; answers: Record<string, unknown>; files?: Array<Record<string, unknown>>; role?: string },
  role?: string
): Promise<ResearchRecommendationsResult> =>
  apiRequest<ResearchRecommendationsResult>(
    '/request-gateway/research/recommendations',
    {
      method: 'POST',
      channel: 'platform',
      query: { role },
      body: payload,
    }
  );

export const submitResearchRequest = (
  payload: ResearchSubmitPayload,
  role?: string
): Promise<ResearchSubmitResult> =>
  apiRequest<ResearchSubmitResult>('/request-gateway/research/submit', {
    method: 'POST',
    channel: 'platform',
    query: { role },
    body: payload,
  });

export const fetchAuthorArticles = (): Promise<AuthorArticle[]> =>
  apiRequest<AuthorArticle[]>('/request-gateway/author/articles', {
    channel: 'platform',
  });

export const reserveAuthorSeat = (
  articleId: string,
  slotRole: string,
  notes?: string
): Promise<{
  success: boolean;
  reservation_code: string;
  article_title: string;
  slot_role: string;
  remaining_slots: number;
  message: string;
}> =>
  apiRequest<{
    success: boolean;
    reservation_code: string;
    article_title: string;
    slot_role: string;
    remaining_slots: number;
    message: string;
  }>('/request-gateway/author/reserve-seat', {
    method: 'POST',
    channel: 'platform',
    body: { article_id: articleId, slot_role: slotRole, notes },
  });

export const saveAuthorRouteDraft = (payload: {
  route_key: string;
  step: number;
  data: Record<string, unknown>;
}): Promise<{
  id: string;
  route_key: string;
  step: number;
  data: Record<string, unknown>;
  updated_at: string;
}> =>
  apiRequest<{
    id: string;
    route_key: string;
    step: number;
    data: Record<string, unknown>;
    updated_at: string;
  }>('/request-gateway/author/draft', {
    method: 'POST',
    channel: 'platform',
    body: payload,
  });

export const getAuthorRouteDraft = async (
  route_key: string
): Promise<{
  id: string;
  route_key: string;
  step: number;
  data: Record<string, unknown>;
  updated_at: string;
} | null> => {
  const result = await apiRequest<{
    id?: string;
    route_key?: string;
    step?: number;
    data?: Record<string, unknown>;
    updated_at?: string;
  } | null>('/request-gateway/author/draft', {
    channel: 'platform',
    query: { route_key },
  });
  if (!result || !result.id || !result.route_key) return null;
  return {
    id: result.id,
    route_key: result.route_key,
    step: result.step ?? 0,
    data: result.data ?? {},
    updated_at: result.updated_at ?? '',
  };
};

export const submitAuthorInquiry = (payload: {
  route_type: string;
  title: string;
  description?: string;
  database_preference?: string;
  scientific_area?: string;
  answers?: Record<string, unknown>;
}): Promise<{
  request_id: string;
  request_number: string;
  title: string;
  status: string;
  message: string;
}> =>
  apiRequest<{
    request_id: string;
    request_number: string;
    title: string;
    status: string;
    message: string;
  }>('/request-gateway/author/submit', {
    method: 'POST',
    channel: 'platform',
    body: payload,
  });

export const createEditorialProject = (
  payload: Partial<EditorialProject> & { as_draft?: boolean }
): Promise<EditorialProject> =>
  apiRequest<EditorialProject>('/request-gateway/editorial/projects', {
    method: 'POST',
    channel: 'platform',
    body: payload,
  });

export const fetchEditorialProjects = (): Promise<EditorialProject[]> =>
  apiRequest<EditorialProject[]>('/request-gateway/editorial/projects', {
    channel: 'platform',
  });

export const updateEditorialProject = (
  projectId: string,
  payload: Partial<EditorialProject> & {
    step?: number;
    data?: Record<string, unknown>;
    accepted?: Record<string, unknown>;
  }
): Promise<EditorialProject> =>
  apiRequest<EditorialProject>(`/request-gateway/editorial/projects/${projectId}`, {
    method: 'PATCH',
    channel: 'platform',
    body: payload,
  });

export const submitEditorialProject = (projectId: string): Promise<EditorialProject> =>
  apiRequest<EditorialProject>(`/request-gateway/editorial/projects/${projectId}/submit`, {
    method: 'POST',
    channel: 'platform',
  });

export const moderateEditorialProject = (
  projectId: string,
  action: string,
  comment?: string
): Promise<EditorialProject> =>
  apiRequest<EditorialProject>(`/request-gateway/editorial/projects/${projectId}/moderate`, {
    method: 'POST',
    channel: 'platform',
    body: { action, comment },
  });

export const fetchFeedPosts = (
  postType?: string,
  favoritesOnly = false
): Promise<FeedPost[]> =>
  apiRequest<FeedPost[]>('/request-gateway/content-publisher/feed', {
    channel: 'platform',
    query: {
      post_type: postType,
      favorites_only: favoritesOnly ? 'true' : undefined,
    },
  });

export const createFeedPost = (
  payload: any,
  role?: string
): Promise<FeedPost> =>
  apiRequest<FeedPost>('/request-gateway/content-publisher/posts', {
    method: 'POST',
    channel: 'platform',
    query: { role },
    body: payload,
  });

export const voteFeedPoll = (
  postId: string,
  optionIndex: number
): Promise<{ success: boolean; message: string }> =>
  apiRequest<{ success: boolean; message: string }>(
    `/request-gateway/content-publisher/posts/${postId}/vote`,
    {
      method: 'POST',
      channel: 'platform',
      body: { option_index: optionIndex },
    }
  );

export const toggleFeedFavorite = (
  postId: string
): Promise<{ is_favorite: boolean; message: string }> =>
  apiRequest<{ is_favorite: boolean; message: string }>(
    `/request-gateway/content-publisher/posts/${postId}/favorite`,
    {
      method: 'POST',
      channel: 'platform',
    }
  );

export const deleteFeedPost = (
  postId: string
): Promise<{ success: boolean; message: string }> =>
  apiRequest<{ success: boolean; message: string }>(
    `/request-gateway/content-publisher/posts/${postId}`,
    {
      method: 'DELETE',
      channel: 'platform',
    }
  );
